import { Money, SlLineGraphData } from '@varo/graphql-schema-types';
import { scaleLinear } from 'd3-scale';
import { curveMonotoneX, line as d3Line } from 'd3-shape';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import Svg, { Circle, G, Line, Path, Text } from 'react-native-svg';
import { handleLayoutEvent } from 'shared/components/SmartViews/Charts/layoutUtils';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import theme from 'shared/styles/theme';
import { VaroFontNames } from 'shared/styles/typography';
import { getVXLColor } from 'shared/utils/getVXLColor/getVXLColor';

import Bullet from './Bullet';
import { toNumber } from './LineChart.helper';

interface DataPoint {
  x: string;
  y: Money;
}

export interface LineChartProps {
  data: SlLineGraphData;
}

const CustomCircle = ({
  lineIndex,
  data,
  lineColor,
  xScale,
  yScale,
}: {
  lineIndex: number;
  data: SlLineGraphData;
  lineColor: string;
  xScale: (value: number) => number;
  yScale: (value: number) => number;
}) => {
  const lineData = data.lines[lineIndex].data;

  if (lineData.length === 0) {
    return (
      <>
        <Circle
          cx={xScale(1)}
          cy={yScale(0)}
          r={8}
          fill={lineColor}
          fillOpacity={0.2}
        />
        <Circle cx={xScale(1)} cy={yScale(0)} r={4} fill={lineColor} />
      </>
    );
  }
  if (lineIndex === data.lines.length - 1 && lineData.length > 0) {
    const lastPoint = lineData[lineData.length - 1];
    return (
      <>
        <Circle
          cx={xScale(new Date(lastPoint.x).getUTCDate())}
          cy={yScale(toNumber(lastPoint.y.amount))}
          r={8}
          fill={lineColor}
          fillOpacity={0.2}
        />
        <Circle
          cx={xScale(new Date(lastPoint.x).getUTCDate())}
          cy={yScale(toNumber(lastPoint.y.amount))}
          r={4}
          fill={lineColor}
        />
      </>
    );
  }

  return <View />;
};

const LineChart = ({ data }: LineChartProps) => {
  const { t: translatedText } = useTranslation('lineChart', {
    keyPrefix: 'daySuffixes.suffixes',
  });

  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const margin = { top: 25, bottom: 25, left: 16, right: 50 };

  const xScale = scaleLinear()
    .domain([1, 31])
    .range([margin.left, containerSize.width - margin.right]);

  const yScale = scaleLinear()
    .domain([0, data.yScale.maxValue])
    .nice()
    .range([containerSize.height - margin.bottom, margin.top]);

  const linesWithPadding = data.lines.map((line, index) => {
    if (index === data.lines.length - 1 || line.data.length === 0) {
      return line;
    }

    const lastPoint = line.data[line.data.length - 1];
    const lastDate = new Date(lastPoint.x);
    const lastDay = lastDate.getUTCDate();

    if (lastDay < 31) {
      const paddedDate = new Date(Date.UTC(2024, 0, 31));
      return {
        ...line,
        data: [
          ...line.data,
          {
            x: paddedDate.toISOString(),
            y: lastPoint.y,
          },
        ],
      };
    }

    return line;
  });

  const linePaths = linesWithPadding.map((line) => {
    if (line.data.length > 1) {
      return (
        d3Line<DataPoint>()
          .x((d) => xScale(new Date(d.x).getUTCDate()))
          .y((d) => yScale(toNumber(d.y.amount)))
          .curve(curveMonotoneX)(line.data) ?? undefined
      );
    } else if (line.data.length === 1) {
      const singlePoint = line.data[0];
      return `M${xScale(1)},${yScale(0)} L${xScale(new Date(singlePoint.x).getDate())},${yScale(toNumber(singlePoint.y.amount))}`;
    }
    return '';
  });

  const formatDay = (day: number) => {
    const suffixes = [
      translatedText('th'),
      translatedText('st'),
      translatedText('nd'),
      translatedText('rd'),
    ];
    const value = day % 10;
    return day + (suffixes[value] || translatedText('th'));
  };

  const displayedDays = [1, 10, 20, 30];

  return (
    <>
      <View
        style={styles.container}
        onLayout={(event) => handleLayoutEvent(event, setContainerSize)}
      >
        <Svg
          width={containerSize.width}
          height={containerSize.height}
          accessibilityLabel="line-chart"
        >
          {/* X Axis */}
          <G>
            <Line
              x1={margin.left}
              y1={containerSize.height - margin.bottom}
              x2={containerSize.width - margin.right}
              y2={containerSize.height - margin.bottom}
              stroke={theme.color.border.primary}
            />
            {displayedDays.map((day) => (
              <G key={`x-axis-${day}`}>
                <Text
                  x={xScale(day) + 5}
                  y={containerSize.height - margin.bottom + 20}
                  textAnchor="middle"
                  fontSize={12}
                  fill={theme.color.text.secondary}
                  fontFamily={
                    VaroFontNames.NeueHaasGroteskDisplayProRomanNormal
                  }
                >
                  {formatDay(day)}
                </Text>
              </G>
            ))}
          </G>
          {/* Y Axis */}
          <G>
            {data.yScale.labels.map((label, i) => (
              <G key={`y-axis-${label.value}`}>
                <Line
                  x1={margin.left}
                  y1={yScale(label.value)}
                  x2={containerSize.width - margin.right}
                  y2={yScale(label.value)}
                  stroke={theme.color.border.primary}
                />
                <Text
                  x={containerSize.width - margin.right + 5}
                  y={yScale(label.value) + 4}
                  textAnchor="start"
                  fontSize={12}
                  fill={theme.color.text.secondary}
                  fontFamily={
                    VaroFontNames.NeueHaasGroteskDisplayProRomanNormal
                  }
                >
                  {label.title}
                </Text>
                {/* Mid-line between y axis rules */}
                {i < data.yScale.labels.length - 1 ? (
                  <Line
                    x1={margin.left}
                    y1={
                      (yScale(label.value) +
                        yScale(data.yScale.labels[i + 1].value)) /
                      2
                    }
                    x2={containerSize.width - margin.right}
                    y2={
                      (yScale(label.value) +
                        yScale(data.yScale.labels[i + 1].value)) /
                      2
                    }
                    stroke={theme.color.border.primary}
                  />
                ) : null}
              </G>
            ))}
          </G>
          {/* Render each line */}
          {linePaths.map((pathData, index) => {
            const lineColor = getVXLColor(data.lines[index].color);

            return (
              <G key={`line-${data.lines[index].color}`}>
                <Path
                  d={pathData}
                  fill="none"
                  stroke={lineColor}
                  strokeWidth={3}
                  testID={`line-path-${index}`}
                />
                <CustomCircle
                  lineIndex={index}
                  data={data}
                  lineColor={lineColor}
                  xScale={xScale}
                  yScale={yScale}
                />
              </G>
            );
          })}
        </Svg>
      </View>
      <View style={styles.legendsWrapper}>
        {data.lines.map((monthData) => {
          const lineColor = getVXLColor(monthData.color);

          return (
            <View
              key={`legend-${monthData.color}`}
              style={styles.legendCardContainer}
            >
              <Bullet iconSize={15} iconColor={lineColor} />
              <BodyXSText numberOfLines={1} style={styles.legendText}>
                {monthData.title}
              </BodyXSText>
            </View>
          );
        })}
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    height: 218,
    width: '100%',
  },
  legendCardContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    padding: theme.spacing.s,
  },
  legendText: {
    color: theme.color.text.secondary,
    marginLeft: 6,
  },
  legendsWrapper: {
    alignSelf: 'center',
    flexDirection: 'row',
  },
});

export default LineChart;
